import {
  AiChatProps,
  ChatMessage,
  NewAiChatProps,
  NewChatMessage,
} from '@components/ai-input-assist/types';

import { AIModel, AIReasoningEffort, JourneyType } from 'api/types';
import { AttributeFieldType } from 'ui/actions/common';

import { request } from '../request';

export type VectorStoreStatus = 'pending' | 'indexing' | 'ready' | 'failed';

export type AIAgent = {
  createdByName: string;
  description: string;
  isActive: boolean;
  model: AIModel;
  name: string;
  picture?: string;
  files: string[];
  shortId: string;
  systemInstructions: string;
  temperature?: number;
  reasoningEffort?: AIReasoningEffort;
  topP?: number;
  output: {
    name: string;
    label: string;
    type: AttributeFieldType;
    instructions: string;
  }[];
  hasStructuredOutput: boolean;
  useWebSearch: boolean;
  vectorStoreStatus: VectorStoreStatus;
  useFileSearch: boolean;
};

export const loadAIAgents = async () => {
  const response = await request.get<AIAgent[]>('v1/ai-agents/');
  return response.data;
};

export const loadAIAgent = async (shortId: string) => {
  const response = await request.get<AIAgent>(`v1/ai-agents/${shortId}/`);
  return response.data;
};

export type EditAIAgentProps = Pick<
  AIAgent,
  | 'name'
  | 'description'
  | 'systemInstructions'
  | 'model'
  | 'temperature'
  | 'output'
  | 'hasStructuredOutput'
  | 'files'
  | 'picture'
  | 'useWebSearch'
  | 'useFileSearch'
> & {
  shortId?: string;
};

export const createAIAgent = async (data: EditAIAgentProps) => {
  const response = await request.post('v1/ai-agents/', data);
  return response.data;
};

export const updateAIAgent = async (data: EditAIAgentProps) => {
  const response = await request.patch(`v1/ai-agents/${data.shortId}/`, data);
  return response.data;
};

export type DuplicateAIAgentData = {
  name: string;
  includeFiles: boolean;
};

export const duplicateAIAgent = async (shortId: string, data: DuplicateAIAgentData) => {
  const response = await request.post(`v1/ai-agents/${shortId}/duplicate/`, data);
  return response.data;
};

export const removeAIAgent = async (shortId: string) => {
  const response = await request.delete(`v1/ai-agents/${shortId}/`);
  return response.data;
};

type TestAIAgentData = {
  [key: string]: any;
  actionId: string;
  additionalFiles: Array<File>;
  websocketId: string;
};

export const testAIAgent = async (data: TestAIAgentData) => {
  const response = await request.post(`v1/ai-agents/test/`, data);
  return response.data;
};

export const aiAgentJourneys = async (shortId: string) => {
  const response = await request.get<JourneyType[]>(`v1/ai-agents/${shortId}/journeys/`);
  return response.data;
};

export const createNewChat = async (props: NewAiChatProps) => {
  const response = await request.post<NewChatMessage>(`v1/ai-agents/new-chat/`, {
    message: props.prompt,
    variableMap: props.variableMap,
    threadInstructions: props.threadInstructions,
    websocketId: props.websocketId,
  });
  return response.data;
};

export const loadChat = async (threadId: string) => {
  const response = await request.get<ChatMessage[]>(`v1/ai-agents/chat/${threadId}/`);
  return response.data;
};

export const sendChat = async (props: AiChatProps) => {
  const { prompt, threadId, variableMap, websocketId } = props;
  const response = await request.post<ChatMessage>(`v1/ai-agents/chat/${threadId}/`, {
    message: prompt,
    variableMap: variableMap,
    websocketId: websocketId,
  });
  return response.data;
};
